/**
 * The bug reports, from the client (netlify/functions/api/bugs.ts). Reading what is published
 * needs no sign-in, so it goes by plain fetch; the rest carries the signed-in person's token
 * through the API client.
 */
import { apiClient } from './apiClient';
import type { BugArea, BugReport, PublicBugReport, ReviewAction } from './bugReports';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export interface Challenge { token: string; question: string }

/** Every published report, newest first */
export const listBugs = async (): Promise<PublicBugReport[]> => {
    const res = await fetch(`${API_BASE}/bugs`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('The reports could not be loaded.');
    const json = await res.json().catch(() => ({}));
    return Array.isArray(json?.reports) ? json.reports : [];
};

/** The signed-in person's own reports, whatever their state */
export const myBugs = async (): Promise<BugReport[]> => (await apiClient.get<{ reports?: BugReport[] }>('/bugs/mine')).reports ?? [];

/** A sum to answer with the report */
export const bugChallenge = (): Promise<Challenge> => apiClient.get<Challenge>('/bugs/challenge');

/** A report sent, with the sum's answer; the server keeps it for review */
export const reportBug = (input: { title: string; details: string; area: BugArea; token: string; answer: string; name?: string }): Promise<{ report: BugReport }> =>
    apiClient.post<{ report: BugReport }>('/bugs', input);

/** Every report, for an admin */
export const reviewList = async (): Promise<BugReport[]> => (await apiClient.get<{ reports?: BugReport[] }>('/bugs/review')).reports ?? [];

/** An admin's decision on a report */
export const reviewBug = (id: string, action: ReviewAction, note?: string): Promise<{ report: BugReport }> =>
    apiClient.post<{ report: BugReport }>('/bugs/review', { id, action, note });

/** What an API refusal says, for the page */
export const bugErrorText = (e: unknown, fallback: string): string => {
    const message = (e as { message?: unknown })?.message;
    return typeof message === 'string' && message && !/^Request failed/i.test(message) ? message : fallback;
};
